import {
  Accordion,
  AccordionDetails,
  Box,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import accordionarrow from 'assets/images/arrow-accordin.svg';
import React, { ReactElement, ReactNode } from 'react';

/**
 * The timesheet box: the period accordion and week cards from the consultant timesheet page.
 *
 * Lifted out of Timesheet.tsx so other screens render the same thing rather than an approximation.
 * Most of the look comes from style.scss - `.common-accordin`, `.accordin-details1`,
 * `.status-details`, `.timesheet-details`, `.accord-active`. Every rule under `.common-accordin`
 * is a descendant selector, which is why the group is a component rather than a class each page
 * remembers to add.
 */

/* ------------------------------------------------------------------ styles */

const styles = {
  /** Left column of a week card; the table takes the remaining 90%. */
  rowLabel: {
    margin: '0 10px 0',
    width: '10%',
  },

  rowTable: {
    width: '90%',
  },

  /** When there is no left label, the table takes the whole card. */
  rowTableFull: {
    width: '100%',
  },

  /** The card keeps the paper background rather than the accordion's. */
  rowWrapper: {
    bgcolor: 'background.paper',
    borderRadius: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    m: 0,
    p: 0,
  },

  /** Turns off the timeline gutter for pages that are a flat list, not a timeline. */
  suppressGutter: {
    '& .MuiAccordion-root::before': { display: 'none' },
    '& .accordin-details-pending::before': { display: 'none' },
    '& .accordin-details1::before': { display: 'none' },
  },

  /** Inherit rather than the MUI default, so the head sits flat on the card. */
  tableHead: {
    backgroundColor: 'inherit !important',
  },

  title: {
    padding: '20px',
  },

  toggle: {
    margin: 15,
    marginBottom: 10,
    marginTop: 10,
  },

  total: {
    fontFamily: 'Red Hat Display Medium',
    textAlign: 'end',
    width: '60%',
  },

  totalValue: {
    fontFamily: 'Red Hat Display Bold',
  },
};

/* ------------------------------------------------------------------- types */

type GroupProps = {
  children: ReactNode;

  /** The timesheet sits in a timeline and keeps the connector rail and paid-status marker.
   *  A flat list leaves this off and both are suppressed. */
  timeline?: boolean;
};

type BoxProps = {
  /** Left of the header. On the timesheet this is the pay period, e.g. "August 02 - 15". */
  label: ReactNode;

  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;

  /** Right of the header, shown only while open. The timesheet uses "Total Hours". */
  totalLabel?: string;
  totalValue?: ReactNode;

  /** A period with payment outstanding takes a different header treatment. */
  pending?: boolean;

  className?: string;
};

type RowProps = {
  /** The narrow left column. On the timesheet this is the week, e.g. "Aug 02 - 08".
   *  Omit it when the table's own first column already carries that, and it takes full width. */
  label?: ReactNode;
  children: ReactNode;

  /** Column headings. Trailing colons are the house convention and are added if omitted. */
  headers?: string[];

  /** Rendered above the table, where the timesheet puts Save Changes / Approve. */
  actions?: ReactNode;
};

type DataRowProps = {
  children: ReactNode;

  /** The timesheet's three states: grey for an empty week, blue once a file is attached,
   *  yellow for hours without one. */
  variant?: 'gray' | 'blue' | 'yellow';
};

/* -------------------------------------------------------------- components */

/** Wraps a set of boxes. Owns the class every box's styling hangs off. */
export const TimesheetBoxGroup = (props: GroupProps): ReactElement => (
  <Typography
    component={'div'}
    className="common-accordin"
    sx={props.timeline ? undefined : styles.suppressGutter}>
    {props.children}
  </Typography>
);

/**
 * A data row inside a week card. The band treatment is a class rather than a style, so it exists
 * as a component instead of something each page has to remember to add.
 */
export const TimesheetBoxDataRow = (props: DataRowProps): ReactElement => (
  <TableRow className={`active-${props.variant ?? 'gray'}`}>
    {props.children}
  </TableRow>
);

/** One week card: a label on the left, a table on the right. */
export const TimesheetBoxRow = (props: RowProps): ReactElement => {
  const { label, children, headers = [], actions } = props;

  return (
    <Typography component={'div'} className="status-details">
      <Typography className="timesheet-details">
        {actions && (
          <Typography component={'div'} className="finalize-approve">
            {actions}
          </Typography>
        )}

        <Typography component={'div'}>
          <Box sx={styles.rowWrapper}>
            {label !== undefined && (
              <Typography sx={styles.rowLabel}>{label}</Typography>
            )}

            <Typography
              sx={label === undefined ? styles.rowTableFull : styles.rowTable}>
              <TableContainer component={'div'}>
                <Table aria-label="simple table">
                  {headers.length > 0 && (
                    <TableHead sx={styles.tableHead}>
                      <TableRow>
                        {headers.map((header: string) => (
                          <TableCell key={header}>
                            {header.endsWith(':') ? header : `${header}:`}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                  )}
                  {children}
                </Table>
              </TableContainer>
            </Typography>
          </Box>
        </Typography>
      </Typography>
    </Typography>
  );
};

/** One period: a header that opens and closes, holding week cards. */
const TimesheetBox = (props: BoxProps): ReactElement => {
  const {
    label,
    expanded,
    onToggle,
    children,
    totalLabel = 'Total',
    totalValue,
    pending = false,
    className,
  } = props;

  return (
    <Accordion expanded={expanded} className={className}>
      <Typography
        component={'div'}
        className={pending ? 'accordin-details-pending' : 'accordin-details1'}>
        <Typography component={'p'} sx={styles.title}>
          {label}
        </Typography>

        {expanded && totalValue !== undefined && (
          <Typography
            component={'p'}
            className="accord-active"
            sx={styles.total}>
            {`${totalLabel}: `}
            <span style={styles.totalValue}>{totalValue}</span>
          </Typography>
        )}

        <a
          href={'javascript:void(0);'}
          onClick={onToggle}
          style={styles.toggle}>
          <img
            src={accordionarrow}
            className={expanded ? 'expand-new' : ''}
            alt=""
          />
        </a>
      </Typography>

      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};

export default TimesheetBox;
